import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Check, Calendar, BarChart3, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const benefits = [{
  title: "Direct consultant access",
  desc: "Work directly with me on strategy, technical priorities and implementation guidance."
}, {
  title: "Commercially focused SEO",
  desc: "Targeting high-intent search terms that attract qualified buyers and generate revenue."
}, {
  title: "Clear priorities and reporting",
  desc: "Actionable written updates and meaningful progress metrics without jargon or vanity numbers."
}];
export default function Hero() {
  return <section id="hero" aria-labelledby="hero-heading" className="relative bg-background text-foreground pt-28 pb-16 md:pt-36 md:pb-24 lg:pt-40 lg:pb-28 border-b border-border/40">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7 flex flex-col items-start">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary mb-4">
              Independent SEO consultancy | UK &amp; international
            </p>

            <h1 id="hero-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.12]">
              Freelance SEO Consultant
            </h1>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              As an independent UK freelance SEO consultant and expert, I provide direct access to me for UK SMEs, ecommerce brands and B2B companies. I focus on high-intent commercial keyword opportunities that attract qualified organic traffic, generating sustainable leads and enquiries. All consultancy is delivered remotely, working closely alongside your team to achieve long-term search growth.
            </p>

            <div className="mt-8 space-y-3.5 w-full max-w-xl">
              {benefits.map((benefit, i) => <div key={i} className="flex items-start gap-3">
                  <div className="size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="size-3.5" strokeWidth={2.5} />
                  </div>
                  <div className="text-sm sm:text-base">
                    <span className="font-semibold text-foreground">{benefit.title}: </span>
                    <span className="text-muted-foreground">{benefit.desc}</span>
                  </div>
                </div>)}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-7 py-6 text-base rounded-md shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-5 shrink-0" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted text-foreground font-medium px-6 py-6 text-base rounded-md">
                <Link to="/ContactPage?topic=free-seo-audit" className="inline-flex items-center justify-center gap-2">
                  <BarChart3 className="size-5 text-primary shrink-0" />
                  <span>Get a Free SEO Audit</span>
                </Link>
              </Button>
            </div>

            <div className="mt-5">
              <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                <Mail className="size-4 text-primary" />
                <span>Email me about your project</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="overflow-hidden rounded-xl border border-border bg-card p-3 shadow-sm">
                <div className="overflow-hidden rounded-lg bg-muted aspect-[4/5]">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi, independent freelance SEO consultant" className="size-full object-cover object-top" decoding="async" />
                </div>
                <p className="mt-3 text-center text-xs font-medium text-muted-foreground">
                  Yassine Essaadi — Independent SEO Consultant
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}